import React, { useCallback, useRef, useState } from "react";
import { Box } from "@mui/material";
import { AgGridReact } from "ag-grid-react";
import AgGridTableWrapper from "../../agGrid/AgGridTableWrapper";
import ToggleTabSwitcher from "../../navigation/ToggleTabSwitcher";
import SearchInput from "../../inputs/SearchInput";
import Flx from "../../layout/Flx";

// Assumes these already exist in your codebase:
/// import Flx from "...";
/// import SearchInput from "...";
/// import ToggleTabSwitcher from "...";
/// import AgGridTableWrapper from "...";
/// import { columnTypesDate } from "...";
/// import ChipItem from "...";
/// import FilePreviewSideModal from "...";

const DEFAULT_GROUP_TYPES = {
  list: "List",
  groupA: "By Group",
  groupB: "By Type",
};

/**
 * Generic reusable selectable table with:
 * - quick filter (SearchInput)
 * - grouping toggle (ToggleTabSwitcher)
 * - selection propagation
 * - row/cell click hooks
 * - group row expand/collapse on row click
 */
export const ReusableSelectableTable = ({
  rowData,
  columnDefs,
  selectedRows,
  setSelectedRows,
  height = "100%",
  groupTypes = DEFAULT_GROUP_TYPES,
  defaultGroupType, // if not provided, uses first value in groupTypes
  quickFilterPlaceholder,
  getRowId,
  columnTypes,
  headerHeight = 0,
  tooltipShowDelay = 100,
  domLayout = "autoHeight",
  groupDisplayType = "groupRows",
  rowSelection = { mode: "multiRow" },
  stopEditingWhenCellsLoseFocus = true,

  onRowClicked,
  onCellClicked,
  getRowStyle,

  // Optional: render additional content below the grid (ex: preview modal)
  renderBottom,
}) => {
  const ref = useRef();

  const [rowGroup, setRowGroup] = useState(
    defaultGroupType || Object.values(groupTypes)[0],
  );
  const [quickFilter, setQuickFilter] = useState(null);

  const handleSelectionChanged = useCallback(
    (params) => {
      if (!setSelectedRows) return;
      const selected = params.api.getSelectedRows();
      setSelectedRows(selected);
    },
    [setSelectedRows],
  );

  const handleRowClicked = useCallback(
    (params) => {
      // Default UX: clicking group headers expands/collapses
      if (params?.node?.group) {
        params.node.setExpanded(!params.node.expanded);
        return;
      }
      if (onRowClicked) onRowClicked(params, { rowGroup });
    },
    [onRowClicked, rowGroup],
  );

  const handleCellClicked = useCallback(
    (params) => {
      if (onCellClicked) onCellClicked(params, { rowGroup });
    },
    [onCellClicked, rowGroup],
  );

  const computedRowStyle = useCallback(
    (params) => {
      // default group header style + allow override/extension
      if (params.node.group) {
        return {
          fontWeight: "600",
          cursor: "pointer",
          ...(getRowStyle ? getRowStyle(params) : {}),
        };
      }
      return getRowStyle ? getRowStyle(params) : undefined;
    },
    [getRowStyle],
  );

  return (
    <Flx column>
      <Flx sx={{ p: 1, borderBottom: "1px solid #e0e0e0" }} fw jb ac wrap>
        <SearchInput
          value={quickFilter}
          placeholder={quickFilterPlaceholder}
          onChange={(e) => setQuickFilter(e)}
        />
        <ToggleTabSwitcher
          value={rowGroup}
          onChange={setRowGroup}
          tabs={Object.values(groupTypes)}
        />
      </Flx>

      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          maxHeight: height,
          borderRadius: "0px",
          ".ag-theme-quartz .ag-root-wrapper": {
            border: "none !important",
            borderLeft: "none !important",
            borderRight: "none !important",
            borderBottom: "none !important",
            borderRadius: "0px !important",
          },
        }}
      >
        <AgGridTableWrapper height={"100%"} compactTable size={4}>
          <AgGridReact
            ref={ref}
            getRowId={getRowId}
            rowData={rowData}
            columnDefs={columnDefs(rowGroup)}
            quickFilterText={quickFilter}
            onRowClicked={handleRowClicked}
            onCellClicked={handleCellClicked}
            getRowStyle={computedRowStyle}
            onSelectionChanged={handleSelectionChanged}
            columnTypes={columnTypes}
            // headerHeight={headerHeight}
            domLayout={domLayout}
            tooltipShowDelay={tooltipShowDelay}
            stopEditingWhenCellsLoseFocus={stopEditingWhenCellsLoseFocus}
            groupDisplayType={groupDisplayType}
            rowSelection={rowSelection}
          />
        </AgGridTableWrapper>
      </Box>

      {renderBottom
        ? renderBottom({ rowGroup, quickFilter, selectedRows })
        : null}
    </Flx>
  );
};
